import { Group, Skeleton } from '@mantine/core';
import React, { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";

type AudiowaveProps = { url: string };

const audioCache = new Map<string, Blob>();
async function fetchAudioBlob(url: string): Promise<Blob> {
	if (audioCache.has(url)) return audioCache.get(url)!;
	const res = await fetch(url, { cache: "force-cache" });
	if (!res.ok) throw new Error("Audio fetch failed");
	const blob = await res.blob();
	audioCache.set(url, blob);
	return blob;
}

const AudioWave: React.FC<AudiowaveProps> = ({ url }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const wavesurferRef = useRef<WaveSurfer>();
	const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isScrubbing, setIsScrubbing] = useState(false);

	useEffect(() => {
		let canceled = false;
		setLoading(true);
		setError(null);

		fetchAudioBlob(url)
			.then(blob => {
				if (canceled) return;
				if (!blob.type.startsWith("audio/")) {
					setError(`Not an audio file (got ${blob.type})`);
					setLoading(false);
				} else {
					setAudioBlob(blob);
				}
			})
			.catch(() => {
				if (!canceled) setError("Failed to load audio.");
			});

		return () => { canceled = true; };
	}, [url]);

	useEffect(() => {
		if (!containerRef.current || wavesurferRef.current) return;

		// Initialize WaveSurfer instance
		const ws = WaveSurfer.create({
			container: containerRef.current,
			waveColor: "#334F73",
			progressColor: "#252841",
			cursorColor: "#E8F1F9",
			cursorWidth: 2,
		});
		ws.on("ready", () => setLoading(false));
		ws.on("error", () => {
			setError("Failed to load audio.");
			setLoading(false);
		});

		wavesurferRef.current = ws;

		return () => {
			// Cleanup WaveSurfer instance
			ws.destroy();
			wavesurferRef.current = undefined;
		};
	}, []);

	useEffect(() => {
		if (wavesurferRef.current && audioBlob) {
			// Update WaveSurfer with new audio blob
			setLoading(true);
			setError(null);
			wavesurferRef.current.loadBlob(audioBlob);
		}
	}, [audioBlob]);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		let isDragging = false;
		function getSeekPercent(e: MouseEvent | TouchEvent) {
			if (!container) return 0;
			const rect = container.getBoundingClientRect();
			const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
			return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
		}

		function onDown(e: MouseEvent | TouchEvent) {
			isDragging = true;
			setIsScrubbing(true);
			const pct = getSeekPercent(e);
			wavesurferRef.current?.seekTo(pct);
			window.addEventListener("mousemove", onMove);
			window.addEventListener("mouseup", onUp);
			window.addEventListener("touchmove", onMove);
			window.addEventListener("touchend", onUp);
		}
		function onMove(e: MouseEvent | TouchEvent) {
			if (isDragging) {
				wavesurferRef.current?.seekTo(getSeekPercent(e));
			}
		}
		function onUp() {
			isDragging = false;
			setIsScrubbing(false);
			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseup", onUp);
			window.removeEventListener("touchmove", onMove);
			window.removeEventListener("touchend", onUp);
		}

		container.addEventListener("mousedown", onDown);
		container.addEventListener("touchstart", onDown);
		return () => {
			container.removeEventListener("mousedown", onDown);
			container.removeEventListener("touchstart", onDown);
			onUp();
		};
	}, []);

	function handlePlayPause() {
		wavesurferRef.current?.playPause();
	}

	return (
		<Group style={{ justifyContent: "center" }}>
			{loading && <Skeleton height={150} width="100%" radius={4} />}
			{error ? (
				<div style={{
					color: "red",
					width: "100%",
					height: 150,
					display: "flex",
					alignItems: "center",
					justifyContent: "center"
				}}>
					{error}
				</div>
			) : (
				<div
					ref={containerRef}
					style={{
						width: "100%",
						height: 150,
						borderRadius: 4,
						cursor: isScrubbing ? "grabbing" : "pointer",
						backgroundColor: "#E8F1F9",
						userSelect: "none",
					}}
					onMouseEnter={handlePlayPause}
					onMouseLeave={handlePlayPause}
				/>
			)}
		</Group>
	);
};

export default AudioWave;
